<template>
  <div id="loginForm">
    <div id="logo">
      <inline-svg :src="$assets.svg['hub_logo_white']" width="100%" height="100%" fill="#fff" />
    </div>

    <div class="messages">
      <span>{{ loginMessage || '&nbsp;' }}</span>
    </div>

    <div class="form-title">Login</div>

    <form @submit.prevent="login" :class="{ submitting: formSubmitting }">
      <div class="login-field field-wrapper">
        <input type="text" placeholder="Sign in with Registered Email" v-model.trim="email" aria-label="email" />
      </div>
      <div class="password-field field-wrapper">
        <input
          type="password"
          placeholder="Password"
          v-model.trim="password"
          @keypress.enter="login"
          aria-label="password"
        />
        <div class="forgot" @click="forgotPassword" @keypress.enter="forgotPassword">Forgot?</div>
      </div>
      <div class="form-opt">
        <button type="submit" class="submit">Login</button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import $assets from '@/assets';
import $session from '@/utils/session';

const $route = useRoute();
const $router = useRouter();
const email = ref(null);
const password = ref(null);
const loginMessage = ref(null);
const formSubmitting = ref(false);

async function login() {
  if (email.value.length === 0) {
    loginMessage.value = 'Please type your user email';
    return;
  }

  if (password.value.length === 0) {
    loginMessage.value = 'Please type your password';
    return;
  }

  loginMessage.value = null;
  formSubmitting.value = true;

  try {
    await $session.login(email.value, password.value, $route.query.backward);
  } catch (err) {
    loginMessage.value = err.message;
    formSubmitting.value = false;
  }
}

function forgotPassword() {
  $router.push({ name: 'PasswordReset' });
}
</script>

<script>
export default {
  name: 'LoginForm',
};
</script>

<style scoped>
#loginForm {
  display: flex;
  flex-direction: column;
  align-items: center;

  position: relative;
  max-width: 90%;
  width: 30em;
  color: #ffffff;
  font-size: 14px;
}

/** HUB Logo SVG */
#logo {
  max-width: 60%;
  width: 20em;
}

/** Form messages */
.messages {
  margin: 1em 0.5em 0 0;
  width: 100%;
  color: #fff220;
  font-size: 1.1em;
  font-style: italic;
  text-align: center;
}

/** Form title */
.form-title {
  width: 100%;
  padding: 0 0 0.5em 0.5em;
  font-size: 1.4em;
  text-align: left;
}

/**
 * Login form elements
 */
#loginForm form {
  width: 100%;
}
#loginForm form.submitting {
  pointer-events: none;
}

/** Form row common styles */

/** Form fields */
form .field-wrapper {
  margin: 0 auto;
  border: 1px solid white;
}
/** Username field */
form .login-field {
  border-radius: 1em 1em 0 0;
  border-bottom: 0 none;
}

/* Password field */
form .password-field {
  position: relative;
  border-radius: 0 0 1em 1em;
}

/** Form input */
form input {
  margin: 0;
  box-sizing: border-box;
  width: 100%;
  border: none;
  padding: 1em 1em;
  background: transparent;
  color: #ffffff;
  font-size: 1.2em;
  outline: none;
}
form input::placeholder {
  color: #ffffff;
  font-style: italic;
  opacity: 0.6;
}

/** Forgot password button */
.forgot {
  display: flex;
  align-items: center;

  position: absolute;
  right: 0;
  bottom: 0;

  height: 100%;
  border-radius: 0 0 0.8em 0;
  padding: 0 1.5em;
  font-style: italic;
  text-align: center;
  cursor: pointer;
}
.forgot:hover {
  background-color: rgba(220, 220, 220, 0.5);
}

/** Operation Buttons */
.form-opt {
  margin-top: 5em;
  width: 100%;
  text-align: center;
}
.form-opt .submit {
  border: none;
  border-radius: 0.3em;
  box-shadow: 0px 0px 0.8em 0.1em rgba(0, 0, 0, 0.178);
  padding: 0.5em 6em;
  background-color: rgba(220, 220, 220, 0.3);
  color: #ffffff;
  font-size: 1.2em;
  cursor: pointer;
  outline: none;
}
.form-opt .submit:hover {
  background-color: rgba(220, 220, 220, 0.6);
}

/** TODO: better solution */
/** For removing chrome's autofill styles */
input:-webkit-autofill,
input:-webkit-autofill:focus {
  transition-delay: 600000s;
  transition: background-color 600000s 0s, color 600000s 0s;
}

/** Landscape mobile */
@media only screen and (orientation: landscape) and (max-height: 600px) {
  #logo {
    width: 15em;
  }
}

/** Larger devices */
@media only screen and (min-width: 601px) and (min-height: 601px) {
  #loginForm {
    width: 42em;
  }

  #logo {
    width: 22em;
    margin-bottom: 2em;
  }

  .messages {
    font-size: 1.2em;
  }

  form input {
    padding: 1.4em 1em;
  }

  .forgot {
    font-size: 1.2em;
  }

  .form-opt {
    margin-top: 8em;
  }

  .form-opt .submit {
    padding: 0.6em 8em;
  }
}
</style>
